import type { CanvasSource, Coordinates, Map as MapLibreMap } from 'maplibre-gl';
import { mercatorLat, mercatorY } from '@/core/utils';

/** World size at zoom 0 in MapLibre's screen px (512 px tiles). */
const WORLD_PX = 512;
const MAX_SIDE = 4096;
/** The canvas is redrawn at the new scale after zooming this much. */
const REFIT_ZOOM = 0.5;

/** What a canvas covers: Web Mercator bounds (0–1), and its resolution. */
export interface ViewFrame {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** Mercator units per canvas pixel. */
  unit: number;
  /** Canvas pixels per screen pixel, at `zoom`. */
  scale: number;
  zoom: number;
}

export interface ViewCanvasOptions {
  /** Id of its canvas source and raster layer. */
  id: string;
  /** Layer to insert it below; on top if not given. */
  beforeId?: string;
  opacity: number;
  /** Covers the view plus this much of its size on every side, so panning reveals no edge. */
  padding: number;
  /** Canvas pixels per screen pixel, for a canvas of `screenPx` screen pixels in all. */
  resolution: (screenPx: number) => number;
  /** Refits carry the picture over (moved and scaled to the new frame); otherwise the canvas starts blank. */
  keepPicture: boolean;
  /** Called after each refit; `previous` is null when starting over rather than carrying on. */
  onFit: (frame: ViewFrame, previous: ViewFrame | null) => void;
}

/** The part of the world in view, Web Mercator (0–1), grown by `pad` of its size on every side. */
function viewRegion(map: MapLibreMap, pad: number) {
  const b = map.getBounds();
  const x0 = (b.getWest() + 180) / 360;
  const x1 = (b.getEast() + 180) / 360;
  const y0 = mercatorY(Math.min(85, b.getNorth()));
  const y1 = mercatorY(Math.max(-85, b.getSouth()));
  const dx = (x1 - x0) * pad;
  const dy = (y1 - y0) * pad;
  return {
    x0: x0 - dx,
    x1: x1 + dx,
    y0: Math.max(mercatorY(85), y0 - dy),
    y1: Math.min(mercatorY(-85), y1 + dy),
  };
}

/**
 * A canvas covering the view and some margin, shown on the map as a canvas source: it sits on the map (and
 * its 3D terrain) under the labels and markers above it. As the view nears its edge or the zoom changes,
 * it moves to the new view within the same frame, so nothing shows the switch.
 */
export class ViewCanvas {
  readonly canvas = document.createElement('canvas');
  readonly ctx: CanvasRenderingContext2D;
  frame: ViewFrame = { x0: 0, y0: 0, x1: 0, y1: 0, unit: 1, scale: 1, zoom: 0 };
  /** Holds the old picture while the canvas is refitted (resizing a canvas clears it). */
  private readonly scratch = document.createElement('canvas');
  private running = false;

  constructor(
    private readonly map: MapLibreMap,
    private readonly options: ViewCanvasOptions,
  ) {
    this.ctx = this.canvas.getContext('2d')!;
    const { id, beforeId, opacity } = options;
    // Placed properly once started; a canvas source needs some coordinates and size from the outset.
    map.addSource(id, { type: 'canvas', canvas: this.canvas, coordinates: this.fitCanvas(), animate: false });
    map.addLayer(
      {
        id,
        type: 'raster',
        source: id,
        layout: { visibility: 'none' },
        paint: { 'raster-opacity': opacity, 'raster-fade-duration': 0, 'raster-resampling': 'linear' },
      },
      beforeId,
    );
    map.on('move', this.onMove);
    map.on('moveend', this.onMoveEnd);
    map.on('resize', this.onMoveEnd);
  }

  /** Shows it (redrawn by the map every frame) or hides it. */
  setRunning(running: boolean) {
    if (running === this.running) return;
    this.running = running;
    this.map.setLayoutProperty(this.options.id, 'visibility', running ? 'visible' : 'none');
    if (running) {
      this.refit(false);
      this.source().play();
    } else {
      this.source().pause();
    }
  }

  dispose() {
    this.map.off('move', this.onMove);
    this.map.off('moveend', this.onMoveEnd);
    this.map.off('resize', this.onMoveEnd);
    // Gone already if the map itself was removed.
    if (this.map.getLayer(this.options.id)) this.map.removeLayer(this.options.id);
    if (this.map.getSource(this.options.id)) this.map.removeSource(this.options.id);
  }

  private source() {
    return this.map.getSource<CanvasSource>(this.options.id)!;
  }

  /** While moving: refit once the view leaves the canvas or the zoom has changed a lot. */
  private readonly onMove = () => {
    if (!this.running) return;
    const view = viewRegion(this.map, 0);
    const f = this.frame;
    const inside = view.x0 >= f.x0 && view.x1 <= f.x1 && view.y0 >= f.y0 && view.y1 <= f.y1;
    if (!inside || Math.abs(this.map.getZoom() - f.zoom) > REFIT_ZOOM) this.refit(true);
  };

  /** Once still: refit for the final zoom, so it's drawn at full sharpness. */
  private readonly onMoveEnd = () => {
    if (!this.running) return;
    if (this.map.getZoom() !== this.frame.zoom) this.refit(true);
    else this.onMove();
  };

  private refit(carryOn: boolean) {
    const previous = this.frame;
    this.source().setCoordinates(this.fitCanvas(carryOn && this.options.keepPicture));
    this.options.onFit(this.frame, carryOn ? previous : null);
  }

  /** Sizes the canvas to the view (carrying the picture over if asked) and returns its corners. */
  private fitCanvas(keepPicture = false): Coordinates {
    const zoom = this.map.getZoom();
    const { x0, x1, y0, y1 } = viewRegion(this.map, this.options.padding);
    const worldPx = WORLD_PX * 2 ** zoom;
    const w = (x1 - x0) * worldPx;
    const h = (y1 - y0) * worldPx;
    const scale = Math.min(this.options.resolution(w * h), MAX_SIDE / w, MAX_SIDE / h);
    const old = this.frame;
    const { canvas, scratch } = this;

    if (keepPicture) {
      scratch.width = canvas.width;
      scratch.height = canvas.height;
      scratch.getContext('2d')!.drawImage(canvas, 0, 0);
    }
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const unit = (x1 - x0) / canvas.width;
    this.frame = { x0, y0, x1, y1, unit, scale, zoom };
    if (keepPicture) {
      const k = old.unit / unit;
      this.ctx.drawImage(
        scratch,
        (old.x0 - x0) / unit,
        (old.y0 - y0) / unit,
        scratch.width * k,
        scratch.height * k,
      );
    }

    const lng = (x: number) => x * 360 - 180;
    return [
      [lng(x0), mercatorLat(y0)],
      [lng(x1), mercatorLat(y0)],
      [lng(x1), mercatorLat(y1)],
      [lng(x0), mercatorLat(y1)],
    ];
  }
}

/** Longitude and latitude of a canvas pixel's position (x, y) into `out`. */
export function frameLngLat(f: ViewFrame, x: number, y: number, out: [number, number]) {
  out[0] = (f.x0 + x * f.unit) * 360 - 180;
  out[1] = mercatorLat(f.y0 + y * f.unit);
}
